import React from 'react';
import { Grid } from '@mui/material';

import { Utils } from 'app/main/components';
import { useSettings, useUserData } from 'app/main/components/hooks';
import { ModuleSearchField, RatingField, SearchSelectField, SimpleTextField } from 'app/main/components/fields';
import { type DetailsSectionInfoContentProps } from 'app/main/components/page-layouts';

export default function CompaniesDetailsSectionInfo(props: DetailsSectionInfoContentProps) {
	const { t } = useSettings();
	const userData = useUserData();

	const isProspectOrCustomer = [1, 2].includes(props.model.comp_core_id);
	const isCustomer = props.model.comp_core_id === 2;

	const isErpLinked = !!(props.model.comp_erp_customer_id || props.model.comp_erp_supplier_id);

	const afterCompanyRelationChange = newStatus => {
		if (newStatus) {
			const existingStatusDate = props.model.comp_statuses_dates?.find(
				statusDate => statusDate.stdt_status_id === newStatus.core_id
			);

			if (existingStatusDate?.stdt_date) return;

			const otherDates = props.model.comp_statuses_dates?.filter(
				statusDate => statusDate.stdt_status_id !== newStatus.core_id
			);

			const newStatusDate = {
				...(existingStatusDate || {}),
				stdt_comp_uid: props.model.comp_uid,
				stdt_user_modified_uid: userData.user_uid,
				stdt_date: Utils.today().toISOString(),
				stdt_status_id: newStatus?.core_id,
				stdt_title: newStatus?.core_title
			};

			props.sendChange('comp_statuses_dates', [...(otherDates || []), newStatusDate]);
		}
	};

	return (
		<>
			{/* TITLE */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SimpleTextField
					name="comp_title"
					label={t('DETAILS_COMPANIES_FIELD_TITLE')}
					value={props.model.comp_title}
					isEdit={props.isEdit && !isErpLinked}
					sendChange={props.sendChange}
					logProps={props.logProps}
					required
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			{/* ID CUSTOMER NAV */}
			<Grid size={{ sm: 2, xs: 12 }}>
				<SimpleTextField
					name="comp_erp_customer_id"
					label={t('DETAILS_COMPANIES_FIELD_ERP_CUSTOMER_ID')}
					value={props.model.comp_erp_customer_id}
					isEdit={props.isEdit && !userData.user_role_external}
					sendChange={props.sendChange}
					logProps={props.logProps}
					required={isCustomer}
					validationError={isCustomer ? t('ERROR_TEXT_REQUIRED') : undefined}
				/>
			</Grid>
			{/* ID SUPPLIER NAV */}
			<Grid size={{ sm: 2, xs: 12 }}>
				<SimpleTextField
					name="comp_erp_supplier_id"
					label={t('DETAILS_COMPANIES_FIELD_ERP_SUPPLIER_ID')}
					value={props.model.comp_erp_supplier_id}
					isEdit={props.isEdit && !userData.user_role_external}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* ID CONTACT NAV */}
			<Grid size={{ sm: 2, xs: 12 }}>
				<SimpleTextField
					name="comp_erp_contact_id"
					label={t('DETAILS_COMPANIES_FIELD_ERP_CONTACT_ID')}
					value={props.model.comp_erp_contact_id}
					isEdit={props.isEdit && !userData.user_role_external}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* RELATION CODE */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SearchSelectField
					name="comp_core_id"
					label={t('DETAILS_COMPANIES_FIELD_RELATION')}
					systable="companies_relations"
					isEdit={props.isEdit && !isErpLinked}
					value={props.model.comp_core_id}
					sendChange={props.sendChange}
					afterChange={afterCompanyRelationChange}
					logProps={props.logProps}
					required
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			{/* COMPANY TYPE */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SearchSelectField
					name="comp_coty_id"
					label={t('DETAILS_COMPANIES_FIELD_TYPE')}
					systable="companies_types"
					value={props.model.comp_coty_id}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					afterChange={() => {
						props.sendChange('comp_companies_subtypes', []);
					}}
					logProps={props.logProps}
					required={isProspectOrCustomer}
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			{/* COMPANY SUBTYPES */}
			<Grid size={12}>
				<SearchSelectField
					name="comp_companies_subtypes"
					label={t('DETAILS_COMPANIES_FIELD_SUBTYPES')}
					systable="companies_subtypes"
					value={props.model.comp_companies_subtypes_ids}
					multiple
					// filters={[
					// 	{ filt_field: 'cosy_coty_id', filt_clause: 'in', filt_value: [props.model.comp_coty_id, null] }
					// ]}
					isEdit={props.isEdit && !!props.model.comp_coty_id}
					sendChange={props.sendChange}
					logProps={props.logProps}
					required={isProspectOrCustomer}
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			{/* USER ASSIGNED 1 */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<ModuleSearchField
					name="comp_user_assigned_uid"
					label={t('DETAILS_COMPANIES_FIELD_ASSIGNED_USER')}
					module="users"
					filters={[{ filt_field: 'user_role_external', filt_clause: 'not_equal', filt_value: 1 }]}
					uid={props.model.comp_user_assigned_uid}
					isEdit={props.isEdit && !userData.user_role_external && !isErpLinked}
					sendChange={props.sendChange}
					logProps={props.logProps}
					required={isProspectOrCustomer}
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			{/* USER ASSIGNED 2 */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<ModuleSearchField
					name="comp_user_assigned_2_uid"
					label={t('DETAILS_COMPANIES_FIELD_ASSIGNED_USER_2')}
					module="users"
					filters={[{ filt_field: 'user_role_external', filt_clause: 'equals', filt_value: 1 }]}
					uid={props.model.comp_user_assigned_2_uid}
					isEdit={props.isEdit && !userData.user_role_external}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* REFERENCE */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SimpleTextField
					name="comp_reference"
					label={t('DETAILS_COMPANIES_FIELD_REFERENCE')}
					value={props.model.comp_reference}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* RATING */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<RatingField
					name="comp_rating"
					label={t('DETAILS_COMPANIES_FIELD_RATING')}
					value={props.model.comp_rating}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* VAT NUMBER */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SimpleTextField
					name="comp_vatnumber"
					label={t('DETAILS_COMPANIES_FIELD_VAT')}
					value={props.model.comp_vatnumber}
					isEdit={props.isEdit && !props.model.comp_erp_customer_id && !props.model.comp_erp_supplier_id}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* TAX CODE */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SimpleTextField
					name="comp_taxcode"
					label={t('DETAILS_COMPANIES_FIELD_TAXCODE')}
					value={props.model.comp_taxcode}
					isEdit={props.isEdit && !props.model.comp_erp_customer_id && !props.model.comp_erp_supplier_id}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
		</>
	);
}
